import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import CustomerHeader from "../../components/CustomerHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Star, Plus, ShoppingCart, ArrowLeft, LogIn, Clock, CalendarClock, X } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `€${Number(n || 0).toFixed(2)}`;

export default function BusinessPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem, count, subtotal } = useCart();
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [booking, setBooking] = useState(null); // {item, variant}
  const [bkDate, setBkDate] = useState(new Date().toISOString().slice(0, 10));
  const [bkTime, setBkTime] = useState("10:00");

  useEffect(() => {
    api.get(`/businesses/${id}`).then(({ data }) => setData(data)).catch(() => toast.error("Attività non disponibile"));
  }, [id]);
  if (!data) return <div className="min-h-screen bg-[#0A0A0C]"><div className="p-8 text-zinc-500">Caricamento…</div></div>;

  const { business, reviews } = data;
  const bizName = business.brand_name || business.name;
  const catalog = data.catalog || [...(data.products || []), ...(data.services || [])];
  const PRODUCT_TYPES = ["prodotto", "product", "menu"];
  const products = data.products || catalog.filter((c) => PRODUCT_TYPES.includes(c.type));
  const services = data.services || catalog.filter((c) => !PRODUCT_TYPES.includes(c.type));
  const biz = { id: business.id, name: bizName };

  const cats = [...new Set(products.map((p) => p.category).filter(Boolean))];
  const cat = activeCat || cats[0] || null;
  const shownProducts = cat ? products.filter((p) => p.category === cat) : products;

  const add = (item, variant = null, slot = null) => {
    const base = variant ? `${item.id}:${variant.name}` : item.id;
    const pid = slot ? `${base}:${slot}` : base;
    const name = (variant ? `${item.name} — ${variant.name}` : item.name) + (slot ? ` (${slot})` : "");
    const price = variant ? variant.price : item.price;
    const isSvc = !PRODUCT_TYPES.includes(item.type);
    addItem({ id: pid, name, price, kind: isSvc ? "service" : undefined, duration_min: item.duration_min, service_id: item.id, slot }, biz);
    toast.success(`${name} aggiunto`);
  };

  const TIMES = [];
  for (let h = 9; h <= 19; h++) { TIMES.push(`${String(h).padStart(2, "0")}:00`); if (h < 19) TIMES.push(`${String(h).padStart(2, "0")}:30`); }
  const confirmBooking = () => {
    add(booking.item, booking.variant, `${bkDate} ${bkTime}`);
    setBooking(null);
  };

  const ItemCard = ({ item, isService }) => (
    <div data-testid={`item-${item.id}`} className="flex gap-4 bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
      {item.image && <img src={item.image} alt={item.name} className="h-20 w-20 rounded-lg object-cover shrink-0 border border-white/10" />}
      <div className="flex-1 min-w-0">
        <div className="font-semibold flex items-center gap-2 flex-wrap">
          {item.name}
          {isService && item.duration_min ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-[#7DF9FF] bg-[#7DF9FF]/10 border border-[#7DF9FF]/30 rounded-full px-2 py-0.5"><Clock className="h-3 w-3" /> {item.duration_min} min</span>
          ) : null}
        </div>
        {item.description && <div className="text-sm text-zinc-400 mt-1 line-clamp-2">{item.description}</div>}
        {item.variants && item.variants.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {item.variants.map((v, i) => (
              <button key={i} data-testid={`variant-${item.id}-${i}`} disabled={!item.available} onClick={() => isService ? setBooking({ item, variant: v }) : add(item, v)}
                className="text-xs rounded-full border border-[#00E676]/40 text-[#00E676] px-3 py-1 hover:bg-[#00E676]/10 disabled:opacity-40 flex items-center gap-1">
                {v.name} · {money(v.price)}{v.duration_min ? ` · ${v.duration_min}m` : ""}<Plus className="h-3 w-3" />
              </button>
            ))}
          </div>
        ) : (
          <div className="text-[#00E676] font-bold mt-2">{money(item.price)}</div>
        )}
        {!item.available && <Badge className="mt-2 bg-red-500/20 text-red-400 border border-red-500/30">{item.availability || "Non disponibile"}</Badge>}
      </div>
      {(!item.variants || item.variants.length === 0) && (
        <div className="flex items-end">
          <Button data-testid={`add-item-${item.id}`} disabled={!item.available} onClick={() => isService ? setBooking({ item }) : add(item)}
            className="bg-[#00E676] text-black hover:bg-[#00c766] rounded-full h-9 px-3 flex items-center gap-1">
            {isService ? <><CalendarClock className="h-4 w-4" /> Prenota</> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0C] grain pb-24">
      {user ? <CustomerHeader /> : (
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
            <button data-testid="back-to-map" onClick={() => navigate("/esplora")} className="flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white"><ArrowLeft className="h-4 w-4" /> Mappa</button>
            <button data-testid="public-login" onClick={() => navigate("/login")} className="flex items-center gap-1.5 text-sm font-semibold text-[#00E676]"><LogIn className="h-4 w-4" /> Accedi</button>
          </div>
        </header>
      )}
      <div className="relative h-56 sm:h-72">
        {business.image ? <img src={business.image} alt={bizName} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-[#1C1C1F] to-[#0A0A0C]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-black/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-6xl mx-auto px-4 lg:px-8 pb-6">
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>{bizName}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm">
            <span className="flex items-center gap-1 text-[#00E676]"><Star className="h-4 w-4 fill-[#00E676]" /> {business.rating || 0}</span>
            <span className="text-zinc-300">· {business.zone} {business.category ? `· ${business.category}` : ""}</span>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 lg:px-8 py-6 relative z-10">
        {products.length === 0 && services.length === 0 && (
          <div className="text-zinc-500 text-sm bg-[#131316] border border-white/10 rounded-xl p-6 text-center" data-testid="empty-catalog">Il catalogo di questa attività non è ancora disponibile.</div>
        )}

        {products.length > 0 && (
          <section data-testid="products-section">
            <h2 className="font-display font-bold text-2xl mb-3">Prodotti</h2>
            {cats.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-4 mb-4 border-b border-white/10">
                {cats.map((c) => (
                  <button key={c} data-testid={`category-${c}`} onClick={() => setActiveCat(c)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm transition-colors ${c === cat ? "bg-[#00E676] text-black font-semibold" : "bg-[#1C1C1F] text-zinc-400 hover:text-white"}`}>{c}</button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {shownProducts.map((p) => <ItemCard key={p.id} item={p} isService={false} />)}
            </div>
          </section>
        )}

        {services.length > 0 && (
          <section data-testid="services-section" className="mt-8">
            <h2 className="font-display font-bold text-2xl mb-3">Servizi</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((s) => <ItemCard key={s.id} item={s} isService={true} />)}
            </div>
          </section>
        )}

        <div className="mt-10">
          <h2 className="font-display font-bold text-2xl mb-4">Recensioni</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(reviews || []).map((r) => (
              <div key={r.id} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{r.customer_name}</span>
                  <span className="flex items-center gap-0.5 text-[#00E676] text-sm"><Star className="h-3 w-3 fill-[#00E676]" /> {r.rating}</span>
                </div>
                <p className="text-sm text-zinc-400 mt-1">{r.comment}</p>
              </div>
            ))}
            {(reviews || []).length === 0 && <div className="text-zinc-500 text-sm">Nessuna recensione.</div>}
          </div>
        </div>
      </main>

      {booking && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center p-3" onClick={() => setBooking(null)} data-testid="booking-modal">
          <div className="bg-[#1C1C1F] border border-white/12 rounded-2xl p-4 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#7DF9FF]" /> Prenota</div>
              <button onClick={() => setBooking(null)}><X className="h-5 w-5 text-zinc-400" /></button>
            </div>
            <div className="text-sm text-zinc-300 mb-3">{booking.variant ? `${booking.item.name} — ${booking.variant.name}` : booking.item.name}
              {booking.item.duration_min ? <span className="text-[#7DF9FF]"> · {booking.item.duration_min} min</span> : null}
            </div>
            <label className="block text-xs text-zinc-400 mb-1">Data</label>
            <input type="date" data-testid="booking-date" value={bkDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setBkDate(e.target.value)} className="w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-2 text-sm mb-3" />
            <label className="block text-xs text-zinc-400 mb-1">Orario</label>
            <div className="grid grid-cols-4 gap-1.5 mb-4 max-h-40 overflow-y-auto">
              {TIMES.map((t) => (
                <button key={t} data-testid={`booking-time-${t}`} onClick={() => setBkTime(t)}
                  className={`text-xs rounded-lg py-1.5 border ${bkTime === t ? "bg-[#00E676] text-black border-[#00E676] font-semibold" : "border-white/10 text-zinc-300"}`}>{t}</button>
              ))}
            </div>
            <Button data-testid="booking-confirm" onClick={confirmBooking} className="w-full bg-[#00E676] text-black hover:bg-[#00c766] font-semibold rounded-full h-11">Aggiungi al carrello · {bkDate} {bkTime}</Button>
          </div>
        </div>
      )}

      {count > 0 && (
        <div className="fixed bottom-4 left-0 right-0 z-40 px-4">
          <div className="max-w-6xl mx-auto">
            <Button data-testid="go-checkout" onClick={() => navigate("/app/checkout")}
              className="w-full bg-[#00E676] text-black hover:bg-[#00c766] font-semibold rounded-full h-12 flex items-center justify-between px-6">
              <span className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> {count} nel carrello</span>
              <span>{money(subtotal)} · Vai al carrello</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
